export enum NotificationType {
  // Combat events
  UNIT_DESTROYED = 'unit_destroyed',
  UNIT_DAMAGED = 'unit_damaged',
  UNIT_HEALED = 'unit_healed',
  
  // City events
  CITY_CAPTURED = 'city_captured',
  CITY_LOST = 'city_lost',
  CITY_CAPTURE_STARTED = 'city_capture_started',
  CITY_CAPTURE_INTERRUPTED = 'city_capture_interrupted',
  
  // Training events
  UNIT_TRAINING_COMPLETE = 'unit_training_complete',
  UNIT_TRAINING_STARTED = 'unit_training_started',
  
  // Mine events
  MINE_DETONATED = 'mine_detonated',
  MINE_DESTROYED_UNIT = 'mine_destroyed_unit',
  MINE_PLACED = 'mine_placed',
  
  // Transport events
  UNIT_BOARDED = 'unit_boarded',
  UNIT_DISEMBARKED = 'unit_disembarked',
  PARATROOPERS_DEPLOYED = 'paratroopers_deployed',
  
  // Turn events
  TURN_START = 'turn_start',
  TURN_END = 'turn_end',
  
  // Connection events
  PLAYER_CONNECTED = 'player_connected',
  PLAYER_DISCONNECTED = 'player_disconnected',
  
  // Victory events
  GAME_WON = 'game_won',
  GAME_LOST = 'game_lost',
  
  // Stance events
  UNIT_HUNKERED = 'unit_hunkered',
  UNIT_STOOD_UP = 'unit_stood_up',
  
  // Veteran events
  UNIT_VETERAN_PROMOTION = 'unit_veteran_promotion',
  
  // UI events
  UNIT_CYCLE = 'unit_cycle',
}

export interface NotificationEvent {
  type: NotificationType;
  faction: string; // Which faction triggered the event
  targetFaction?: string; // Which faction is affected (for opponent notifications)
  data: {
    unitName?: string;
    unitType?: string;
    cityName?: string;
    damage?: number;
    position?: { x: number; y: number };
    [key: string]: any;
  };
  timestamp: number;
}

export interface NotificationMessage {
  text: string;
  type: NotificationType;
  priority: 'low' | 'normal' | 'high' | 'critical';
  duration?: number; // How long to show in ms
  sound?: 'success' | 'warning' | 'error' | 'info';
}

// Helper to format notifications based on perspective
export function formatNotification(
  event: NotificationEvent,
  viewerFaction: string,
  isViewer: boolean
): NotificationMessage | null {
  const { type, faction, targetFaction, data } = event;
  const isOwnEvent = faction === viewerFaction;
  const isTargeted = targetFaction === viewerFaction;
  
  let text = '';
  let priority: NotificationMessage['priority'] = 'normal';
  let sound: NotificationMessage['sound'] | undefined;
  
  switch (type) {
    case NotificationType.UNIT_DESTROYED:
      if (isOwnEvent) {
        text = `Your ${data.unitName} was destroyed!`;
        priority = 'high';
        sound = 'error';
      } else if (isTargeted) {
        text = `You destroyed enemy ${data.unitName}!`;
        priority = 'normal';
        sound = 'success';
      } else if (isViewer) {
        text = `${faction} lost ${data.unitName}`;
        priority = 'low';
      }
      break;
      
    case NotificationType.CITY_CAPTURED:
      if (isOwnEvent) {
        text = `You captured ${data.cityName}!`;
        priority = 'critical';
        sound = 'success';
      } else if (isTargeted) {
        text = `You lost ${data.cityName} to the enemy!`;
        priority = 'critical';
        sound = 'error';
      }
      // Don't show enemy city captures - they should discover it themselves
      break;
      
    case NotificationType.UNIT_TRAINING_COMPLETE:
      // Only show training notifications to the player who owns the unit
      // Enemy training should remain hidden for strategic gameplay
      if (isOwnEvent) {
        text = `${data.unitName} completed training in ${data.cityName}`;
        priority = 'normal';
        sound = 'success';
      }
      // Don't show enemy training notifications at all
      break;
      
    case NotificationType.MINE_DETONATED:
      if (isOwnEvent) {
        text = `Your ${data.unitName} detonated a mine!`;
        priority = 'high';
        sound = 'warning';
      } else if (isTargeted) {
        text = `Enemy ${data.unitName} detonated your mine!`;
        priority = 'normal';
        sound = 'info';
      }
      break;
      
    case NotificationType.TURN_START:
      if (isOwnEvent) {
        text = `It's your turn!`;
        priority = 'critical';
        sound = 'info';
      } else if (isViewer) {
        text = `${faction}'s turn`;
        priority = 'normal';
      }
      break;
      
    case NotificationType.PLAYER_DISCONNECTED:
      text = `${faction} disconnected`;
      priority = 'high';
      sound = 'warning';
      break;
      
    case NotificationType.PLAYER_CONNECTED:
      text = `${faction} reconnected`;
      priority = 'normal';
      sound = 'info';
      break;
      
    case NotificationType.UNIT_HUNKERED:
      if (isOwnEvent) {
        text = `${data.unitName} hunkered down (+2 defense, healing)`;
        priority = 'normal';
        sound = 'info';
      }
      break;
      
    case NotificationType.UNIT_STOOD_UP:
      if (isOwnEvent) {
        text = `${data.unitName} stood up from hunker position`;
        priority = 'normal';
        sound = 'info';
      }
      break;
      
    case NotificationType.UNIT_VETERAN_PROMOTION:
      // Only show veteran promotions to the unit owner
      // Enemy veteran promotions should remain hidden
      if (isOwnEvent) {
        text = `⭐ ${data.unitName} has become a VETERAN! (${data.killCount} kills)`;
        priority = 'critical';
        sound = 'success';
      }
      // Don't reveal enemy veteran promotions
      break;
      
    case NotificationType.UNIT_CYCLE:
      // Unit cycle notifications are always shown for the current player
      text = data.message || 'Unit cycled';
      priority = 'normal';
      sound = undefined;
      break;
      
    default:
      return null;
  }
  
  if (!text) return null;
  
  return {
    text,
    type,
    priority,
    duration: priority === 'critical' ? 5000 : priority === 'high' ? 4000 : 3000,
    sound
  };
}